/* eslint-disable indent */
import React, {
    useState,
    useEffect,
    useReducer,
    useCallback,
    useContext,
    useMemo,
} from 'react';
import { Portal } from 'react-portal';
import PropTypes from 'prop-types';
import { Transition } from 'react-transition-group';
import cn from 'classnames';
import { useQuery } from '@apollo/client';

import { analyticService } from 'global/services';
import { useRouterState } from 'app/hooks/useRouterState';

import { DialogAnimated } from 'app/components/Dialog';
import USER_QUERY from 'app/graphql/network/auth/userQuery.graphql';

import {
    getConfirmCustomizationLocales,
} from 'app/views/Basket/components/BasketCustomizationTags.DEPRECATED/basket-customization-locales';

import { useCustomContradictionDefine } from 'app/views/Basket/hooks/useCustomContradictionDefine';
import { hasDishesInCart } from 'app/views/Checkout/hooks/useReadyToEatContradictionCheck';
import { useReadyToEatContradictionDefine } from 'app/views/Checkout/hooks/useReadyToEatContradictionDefine';
import { LocaleContext } from 'app/containers/LocaleProvider';
import { CheckoutStoreContext } from '../../store/checkoutStore.context';


import { StepAuth } from '../StepAuth/StepAuth';
import { StepAddress } from '../StepAddress/StepAddress';
import { StepDatetime } from '../StepDatetime/StepDatetime';
import { StepPayment } from '../StepPayment/StepPayment';


import {
    STEP_PHONE,
    STEP_ADDRESS,
    STEP_DATETIME,
    STEP_PAYMENT,
    STEP_CODE,
    TYPE_CHECKOUT_STEP_BY_STEP,
    EVENTS,
} from '../stepByStepCheckoutConst';
import { useAuthState } from '../stepByStepCheckoutHooks';
import { readyToEatDialogLocales } from '../SingleStepCheckout/single-step-checkout-locales';

import './step-by-step-checkout.scss';


const STEPS_ORDER_AUTHED = [
    STEP_PHONE,
    STEP_ADDRESS,
    STEP_DATETIME,
    STEP_PAYMENT,
];

const STEPS_ORDER_NOT_AUTHED = [
    STEP_PHONE,
    STEP_CODE,
    STEP_ADDRESS,
    STEP_DATETIME,
    STEP_PAYMENT,
];

const STEPS_COMPONENTS = {
    [STEP_PHONE]: StepAuth,
    [STEP_ADDRESS]: StepAddress,
    [STEP_DATETIME]: StepDatetime,
    [STEP_PAYMENT]: StepPayment,
    [STEP_CODE]: StepAuth,
};

const initialState = {
    openedSteps: [],
    isNextStepButtonVisible: true,
};

function reducer(state, action) {
    switch (action.type) {
        case 'INIT':
            return {
                ...state,
                ...action.payload,
            };
        case 'OPEN_NEXT_STEP': {
            const nextOpenedSteps = Array.from(new Set([
                ...state.openedSteps,
                action.payload.nextStep,
            ]));
            return {
                ...state,
                openedSteps: nextOpenedSteps,
            };
        }
        case 'CLOSE_CURRENT_STEP': {
            const nextOpenedSteps = state.openedSteps.slice(0, state.openedSteps.length - 1);
            return {
                ...state,
                openedSteps: nextOpenedSteps,
            };
        }
        case 'GO_TO_ADDRESS_STEP': {
            const addressStepIndex = state.openedSteps.indexOf(STEP_ADDRESS);
            const nextOpenedSteps = addressStepIndex !== -1
                ? state.openedSteps.slice(0, addressStepIndex + 1)
                : ['STEP_PHONE', STEP_ADDRESS];
            return {
                ...state,
                openedSteps: nextOpenedSteps,
            };
        }
        case 'SET_NEXT_STEP_BUTTON_VISIBLE': {
            return {
                ...state,
                isNextStepButtonVisible: Boolean(action.payload),
            };
        }
        case 'GO_TO_FIRST_STEP': {
            return {
                ...state,
                openedSteps: ['STEP_PHONE'],
            };
        }
        default:
            return state;
    }
}


export function StepByStepCheckout(props) {
    const {
        closeCheckout,
        [STEP_PAYMENT]: paymentProps,
        setIsCustomizationIgnored,
        setIsAddressChanged,
    } = props;
    const { checkoutState, dispatch: checkoutDispatch } = useContext(CheckoutStoreContext);
    const { checkoutTypeState: { checkoutType } } = checkoutState;
    const [isNewbie, setIsNewbie] = useState(true);

    const [state, dispatch] = useReducer(reducer, initialState);
    const {
        openedSteps,
        isNextStepButtonVisible,
    } = state;


    const userQuery = useQuery(USER_QUERY, {
        onCompleted: () => {
            setIsAddressChanged(true);
        },
        fetchPolicy: 'cache-first',
        context: {
            step: 'checkout:refetch:SingleStepCheckout',
        },
    });

    const [
        authState,
        setAuthState,
        initialAuthState,
    ] = useAuthState(userQuery);

    const STEPS_ORDER = useMemo(() => {
        const authResult = authState === 'authResult';
        if (authResult) {
            return STEPS_ORDER_AUTHED;
        }
        if (isNewbie) {
            return STEPS_ORDER_NOT_AUTHED;
        }
        return STEPS_ORDER_NOT_AUTHED;
    }, [authState, isNewbie]);

    const [routerState] = useRouterState();
    const { history, location } = routerState;
    const [isCustomizationDialogClosed, setIsCustomizationDialogClosed] = useState(false);

    const {
        isCustomizationDialogShown,
        setIsCustomizationDialogShown,
        dialogIngridient,
    } = useCustomContradictionDefine({ userQuery: { data: userQuery }, cart: paymentProps.cart });

    /**
     * @description Пока активен одностраничный checkout, пошаговый ещё не размонтирован:
     * оба варианта живут одновременно все 600мс перехода. Напоминание о готовых блюдах
     * рендерится в портале и не уезжает вместе с экраном, поэтому уходящий вариант
     * не должен его показывать: иначе пользователь видит поп-ап поверх уезжающего
     * шага, а потом ещё раз на финальном экране одностраничного checkout.
     */
    const isStepByStepCheckoutActive = checkoutType === TYPE_CHECKOUT_STEP_BY_STEP;

    /**
     * @description Напоминание о готовых блюдах показываем только когда шаг адреса
     * доехал на место. Пока он едет, пользователь ничего не может решить, а поп-ап
     * поверх движения шага выглядит как глюк. Заодно это отсекает ситуацию, когда
     * шаг адреса открывается на 400мс перед переходом в одностраничный checkout:
     * напоминание покажет уже одностраничный checkout, на финальном экране.
     */
    const [isAddressStepTransitionEnded, setIsAddressStepTransitionEnded] = useState(false);

    const handleAddressStepTransitionStart = useCallback(() => {
        setIsAddressStepTransitionEnded(false);
    }, []);

    const handleAddressStepTransitionEnd = useCallback(() => {
        setIsAddressStepTransitionEnded(true);
    }, []);

    const isAddressStepOpened = openedSteps[openedSteps.length - 1] === STEP_ADDRESS
        && isAddressStepTransitionEnded;

    const {
        isReadyToEatDialogShown,
        handleRemoveReadyToEatDishes,
        handleRejectReadyToEatDialog,
    } = useReadyToEatContradictionDefine({
        cart: paymentProps.cart,
        dispatch: checkoutDispatch,
        pathname: location.pathname,
        isAddressStepOpened,
    });

    /**
    * Handlers
    */
    const handleGoToFirstStep = useCallback(() => {
        dispatch({
            type: 'GO_TO_FIRST_STEP',
        });
    }, []);

    const handleCloseCurrentStep = useCallback(() => {
        dispatch({
            type: 'CLOSE_CURRENT_STEP',
        });

        const currentStep = openedSteps[openedSteps.length - 1];

        const stepEvent = EVENTS[currentStep].back;
        analyticService.push({
            eventName: stepEvent,
        });
    }, [openedSteps]);

    const handleCloseAllSteps = useCallback(() => {
        const currentStep = openedSteps[openedSteps.length - 1];
        let stepEvent = EVENTS[currentStep].close;

        if (currentStep === STEP_PHONE) {
            stepEvent = authState === 'pincode' ? EVENTS[currentStep].closeCode : EVENTS[currentStep].close;
        }

        analyticService.push({
            eventName: stepEvent,
        });

        closeCheckout();
    }, [closeCheckout, openedSteps, authState]);

    const handleOpenNextStep = useCallback(() => {
        const currentStep = openedSteps[openedSteps.length - 1];
        const currentStepIndex = STEPS_ORDER.indexOf(currentStep);
        const nextIndex = currentStepIndex !== -1
            ? currentStepIndex + 1
            : openedSteps.length;
        const nextStep = STEPS_ORDER[nextIndex];
        if (!nextStep) return;

        dispatch({
            type: 'OPEN_NEXT_STEP',
            payload: {
                nextStep,
            },
        });

        checkoutDispatch({
            type: 'SET_CHECKOUT_ACTIVE_STEP',
            payload: nextStep,
        });

        const stepEvent = EVENTS[nextStep].open;
        analyticService.push({
            eventName: stepEvent,
        });
    }, [openedSteps, checkoutDispatch, STEPS_ORDER]);

    const handleSetIsOldUser = useCallback(() => {
        setIsNewbie(false);
    }, []);

    /**
     * @description Пользователь согласился удалить готовые блюда. Если в корзине
     * остались другие блюда, оформление продолжается: противоречие снято, ждём
     * следующий шаг. Если корзина опустела, продолжать оформление бессмысленно —
     * отправляем пользователя в корзину. Если запрос не прошёл, не решаем за
     * пользователя и остаёмся на текущем шаге.
     */
    const handleConfirmReadyToEatDialog = useCallback(async () => {
        const nextCart = await handleRemoveReadyToEatDishes();

        if (!nextCart) return;

        if (hasDishesInCart(nextCart)) {
            return;
        }

        history.push('/basket');
    }, [handleRemoveReadyToEatDishes, history]);

    const handleUserEnteredCode = useCallback((result) => {
        const eventName = EVENTS[STEP_PHONE].submitCode;
        analyticService.push({
            eventName,
            result,
        });
    }, []);

    /**
    * Open initial overlay
    */
    useEffect(() => {
        const { user } = userQuery;
        const isUserAuthed = Boolean(user);

        const step = isUserAuthed ? STEP_ADDRESS : STEP_PHONE;
        // const step = STEP_PAYMENT;

        const stepIndex = STEPS_ORDER.findIndex((i) => i === step);
        const initialOpenedSteps = STEPS_ORDER.slice(0, stepIndex + 1);

        dispatch({
            type: 'INIT',
            payload: {
                openedSteps: initialOpenedSteps,
            },
        });

        const lastStep = initialOpenedSteps.at(-1);
        checkoutDispatch({
            type: 'SET_CHECKOUT_ACTIVE_STEP',
            payload: lastStep,
        });

        const stepEvent = EVENTS[step].open;

        if (step === STEP_PHONE && initialAuthState === 'pincode') {
            //
        } else {
            analyticService.push({
                eventName: stepEvent,
            });
        }
        // it's didMount
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        const currentStep = openedSteps[openedSteps.length - 1];
        if (currentStep === STEP_PHONE && authState === 'pincode') {
            analyticService.push({
                eventName: EVENTS[currentStep].openCode,
            });
        }
    }, [openedSteps, authState]);

    const { locale } = useContext(LocaleContext);

    const dialogTexts = dialogIngridient
        ? getConfirmCustomizationLocales(dialogIngridient[locale])[locale].dialog.success
        : null;

    return (
        <div styleName="step-by-step__container">
            {STEPS_ORDER.map((stepKey) => {
                const stepIndex = openedSteps.findIndex((sk) => sk === stepKey);
                const isOpen = stepIndex !== -1;
                const hasNextOpenStep = stepIndex < openedSteps.length - 1;

                const Component = STEPS_COMPONENTS[stepKey];
                const stepProps = props[stepKey];

                const childProps = {
                    handleOpenNextStep,
                    handleCloseCurrentStep,
                    handleCloseAllSteps,
                    handleGoToFirstStep,
                    handleSetIsOldUser,

                    currentStepNumber: stepIndex + 1,
                    isNextStepButtonVisible,

                    ...stepProps,
                };

                return (
                    <React.Fragment key={stepKey}>
                        <Transition
                            in={isOpen}
                            timeout={600}
                            key={stepKey}
                            onEnter={stepKey === STEP_ADDRESS ? handleAddressStepTransitionStart : undefined}
                            onExit={stepKey === STEP_ADDRESS ? handleAddressStepTransitionStart : undefined}
                            onEntered={stepKey === STEP_ADDRESS ? handleAddressStepTransitionEnd : undefined}
                        >
                            {(transitionState) => {
                                const containerClasses = cn({
                                    'step-by-step__transition': true,
                                    [transitionState]: true,
                                    'step__transition--shifted': hasNextOpenStep,
                                });

                                return (
                                    <div styleName={containerClasses}>
                                        {transitionState !== 'exited' && (
                                            <Component
                                                {...childProps}
                                                authState={authState}
                                                setAuthState={setAuthState}
                                                handleUserEnteredCode={handleUserEnteredCode}
                                                transitionState={transitionState}
                                            />
                                        )}
                                    </div>
                                );
                            }}
                        </Transition>
                        {dialogTexts && !isCustomizationDialogClosed && (
                            <Portal>
                                <DialogAnimated
                                    {...dialogTexts}
                                    isOpen={Boolean(isCustomizationDialogShown)}
                                    onConfirm={() => {
                                        console.log('confirm');
                                        setIsCustomizationIgnored(true);
                                        setIsCustomizationDialogShown(false);
                                        setIsCustomizationDialogClosed(true);
                                        dispatch({
                                            type: 'SET_IGNORE_DATES_FILTER',
                                            payload: { isNeedToIgnoreDatesFilter: true },
                                        });
                                    }}
                                    onReject={() => {
                                        console.log('reject');
                                        setIsCustomizationIgnored(false);
                                        setIsCustomizationDialogShown(false);
                                        setIsCustomizationDialogClosed(true);
                                        history.push('/basket');
                                    }}
                                />
                            </Portal>
                        )}
                    </React.Fragment>
                );
            })}
            <Portal>
                <DialogAnimated
                    {...readyToEatDialogLocales[locale]}
                    isOpen={Boolean(isReadyToEatDialogShown) && isStepByStepCheckoutActive}
                    onConfirm={handleConfirmReadyToEatDialog}
                    onReject={() => {
                        handleRejectReadyToEatDialog();
                    }}
                    rejectSpecificHandler={() => {
                        handleRejectReadyToEatDialog();
                        dispatch({
                            type: 'GO_TO_ADDRESS_STEP',
                        });
                        checkoutDispatch({
                            type: 'SET_CHECKOUT_ACTIVE_STEP',
                            payload: STEP_ADDRESS,
                        });
                    }}
                />
            </Portal>
        </div>
    );
}

StepByStepCheckout.propTypes = {
    userQuery: PropTypes.shape({
        user: PropTypes.shape({}),
    }).isRequired,
    closeCheckout: PropTypes.func.isRequired,
};
